import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "../components/ui/dialog";
import { toast } from "sonner";
import { Shield, Check, X, ArrowLeft, Loader2, UserCheck, UserX, Clock, Coins, Plus, Settings, Key } from "lucide-react";
import axios from "axios";
import FSIConfigPanel from "../components/FSIConfigPanel";
import FSICostRatesPanel from "../components/FSICostRatesPanel";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [creditRequests, setCreditRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [actionLoading, setActionLoading] = useState(null);
  
  // Password dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  // Add-user dialog state
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "user", approved: true });

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error("Email, name and password are required");
      return;
    }
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setActionLoading("create-user");
    try {
      await axios.post(`${API}/api/auth/admin/users`, newUser, { withCredentials: true });
      toast.success(`User ${newUser.email} created`);
      setAddUserDialogOpen(false);
      setNewUser({ email: "", name: "", password: "", role: "user", approved: true });
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create user";
      toast.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/admin/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/calculator");
      } else {
        toast.error("Failed to load users");
      }
    }
  }, [navigate]);

  const fetchCreditRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/admin/credit-requests`, { withCredentials: true });
      setCreditRequests(res.data);
    } catch (error) {
      console.error("Failed to load credit requests", error);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchUsers(), fetchCreditRequests()]).finally(() => setLoading(false));
  }, [fetchUsers, fetchCreditRequests]);

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API}/api/auth/admin/users/${userId}/approve`, {}, { withCredentials: true });
      toast.success("User approved");
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to approve user: ${msg}`);
      console.error("Approve error:", error.response?.status, error.response?.data, error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm("Are you sure you want to revoke this user's access?")) return;
    setActionLoading(userId);
    try {
      await axios.put(`${API}/api/auth/admin/users/${userId}/reject`, {}, { withCredentials: true });
      toast.success("User access revoked");
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to revoke user: ${msg}`);
      console.error("Reject error:", error.response?.status, error.response?.data, error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveCreditRequest = async (requestId) => {
    setActionLoading(`credit-${requestId}`);
    try {
      await axios.put(`${API}/api/auth/admin/credit-requests/${requestId}/approve`, {}, { withCredentials: true });
      toast.success("Credits approved");
      fetchCreditRequests();
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to approve credits: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectCreditRequest = async (requestId) => {
    setActionLoading(`credit-${requestId}`);
    try {
      await axios.put(`${API}/api/auth/admin/credit-requests/${requestId}/reject`, {}, { withCredentials: true });
      toast.success("Credit request rejected");
      fetchCreditRequests();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to reject request: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddCredits = async (userId) => {
    setActionLoading(`add-${userId}`);
    try {
      const res = await axios.put(`${API}/api/auth/admin/users/${userId}/credits`, {}, { withCredentials: true });
      toast.success(`Added 5 credits. New balance: ${res.data.new_balance}`);
      fetchUsers();
    } catch {
      toast.error("Failed to add credits");
    } finally {
      setActionLoading(null);
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setPasswordDialogOpen(true);
  };

  const handleSetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setActionLoading(`pwd-${selectedUser.user_id}`);
    try {
      await axios.put(
        `${API}/api/auth/admin/users/${selectedUser.user_id}/password`,
        { password: newPassword },
        { withCredentials: true }
      );
      toast.success(`Password set for ${selectedUser.name || selectedUser.email}`);
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword("");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to set password";
      toast.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const pendingUsers = users.filter(u => !u.approved && !u.rejected);
  const approvedUsers = users.filter(u => u.approved);
  const rejectedUsers = users.filter(u => u.rejected);
  const pendingCreditRequests = creditRequests.filter(r => r.status === "pending");
  const [showRejected, setShowRejected] = useState(false);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Permanently delete this user and all their data?")) return;
    setActionLoading(`del-${userId}`);
    try {
      await axios.delete(`${API}/api/auth/admin/users/${userId}`, { withCredentials: true });
      toast.success("User deleted permanently");
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Failed to delete: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAllRejected = async () => {
    if (!window.confirm(`Delete all ${rejectedUsers.length} rejected users permanently?`)) return;
    setActionLoading("del-all");
    try {
      await axios.delete(`${API}/api/auth/admin/users/rejected/all`, { withCredentials: true });
      toast.success("All rejected users deleted");
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Failed: ${msg}`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/calculator")} data-testid="admin-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Admin Panel
          </h1>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b pb-2">
          <Button
            variant={activeTab === "users" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("users")}
            data-testid="admin-tab-users"
          >
            <UserCheck className="h-4 w-4 mr-1" /> Users
          </Button>
          <Button
            variant={activeTab === "fsi-config" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("fsi-config")}
            data-testid="admin-tab-fsi"
          >
            <Settings className="h-4 w-4 mr-1" /> FSI Config
          </Button>
          <Button
            variant={activeTab === "cost-rates" ? "default" : "ghost"}
            size="sm"
            onClick={() => setActiveTab("cost-rates")}
            data-testid="admin-tab-cost-rates"
          >
            <Settings className="h-4 w-4 mr-1" /> Cost Rates
          </Button>
        </div>

        {loading && activeTab === "users" ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : activeTab === "fsi-config" ? (
          <FSIConfigPanel />
        ) : activeTab === "cost-rates" ? (
          <FSICostRatesPanel />
        ) : (
          <>
            {/* Pending Credit Requests */}
            {pendingCreditRequests.length > 0 && (
              <Card className="border-blue-200" data-testid="pending-credits-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Coins className="h-4 w-4 text-blue-600" />
                    Credit Requests ({pendingCreditRequests.length})
                  </CardTitle>
                  <CardDescription>Users requesting additional project credits</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {pendingCreditRequests.map(req => (
                      <div key={req.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-blue-50 rounded border border-blue-200" data-testid={`credit-req-${req.id}`}>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{req.user_name || "User"}</p>
                          <p className="text-xs text-muted-foreground truncate">{req.user_email} &middot; Requesting {req.credits_requested} credits</p>
                          {req.message && <p className="text-xs text-slate-600 mt-1 italic truncate">&ldquo;{req.message}&rdquo;</p>}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="default"
                            disabled={actionLoading === `credit-${req.id}`}
                            onClick={() => handleApproveCreditRequest(req.id)}
                            data-testid={`approve-credit-btn-${req.id}`}
                          >
                            {actionLoading === `credit-${req.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                            Grant +5
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={actionLoading === `credit-${req.id}`}
                            onClick={() => handleRejectCreditRequest(req.id)}
                            data-testid={`reject-credit-btn-${req.id}`}
                          >
                            <X className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pending User Approvals */}
            <Card className="border-amber-200" data-testid="pending-users-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  Pending Approvals ({pendingUsers.length})
                </CardTitle>
                <CardDescription>Users waiting for access</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No pending requests</p>
                ) : (
                  <div className="space-y-2">
                    {pendingUsers.map(user => (
                      <div key={user.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-amber-50 rounded border border-amber-200" data-testid={`pending-user-${user.user_id}`}>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{user.name || "Unnamed"}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email} &middot; {user.auth_provider || "email"}
                            {user.project_count > 0 && <span className="ml-1 text-blue-600 font-medium">&middot; {user.project_count} projects</span>}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="default"
                            disabled={actionLoading === user.user_id}
                            onClick={() => handleApprove(user.user_id)}
                            data-testid={`approve-btn-${user.user_id}`}
                          >
                            {actionLoading === user.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={actionLoading === user.user_id}
                            onClick={() => handleReject(user.user_id)}
                            data-testid={`reject-btn-${user.user_id}`}
                          >
                            <X className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Approved Users */}
            <Card data-testid="approved-users-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-green-600" />
                    Approved Users ({approvedUsers.length})
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => setAddUserDialogOpen(true)}
                    data-testid="admin-add-user-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {approvedUsers.map(user => (
                    <div key={user.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-secondary/30 rounded border" data-testid={`approved-user-${user.user_id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-sm flex items-center gap-1.5 flex-wrap">
                          <span className="truncate">{user.name || "Unnamed"}</span>
                          {user.role === "admin" && <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded flex-shrink-0">ADMIN</span>}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {user.email} &middot; {user.auth_provider || "email"} &middot; 
                          <span className="text-blue-600 font-medium ml-1">
                            <Coins className="h-3 w-3 inline mr-0.5" />{user.project_credits ?? 0} credits
                          </span>
                          <span className="ml-1">&middot; {user.project_count ?? 0} projects</span>
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap flex-shrink-0">
                        {user.role !== "admin" && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              disabled={actionLoading === `add-${user.user_id}`}
                              onClick={() => handleAddCredits(user.user_id)}
                              data-testid={`add-credits-btn-${user.user_id}`}
                            >
                              {actionLoading === `add-${user.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                              +5 Credits
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 border-amber-200 hover:bg-amber-50"
                              onClick={() => openPasswordDialog(user)}
                              data-testid={`set-password-btn-${user.user_id}`}
                            >
                              <Key className="h-3 w-3 mr-1" /> Set Password
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive border-destructive/30 hover:bg-destructive/10"
                              disabled={actionLoading === user.user_id}
                              onClick={() => handleReject(user.user_id)}
                              data-testid={`revoke-btn-${user.user_id}`}
                            >
                              <UserX className="h-3 w-3 mr-1" /> Revoke
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Rejected Users (collapsible) */}
            {rejectedUsers.length > 0 && (
              <Card data-testid="rejected-users-card">
                <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowRejected(!showRejected)}>
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <UserX className="h-4 w-4 text-red-500" />
                      Rejected Users ({rejectedUsers.length})
                    </span>
                    <span className="text-xs text-muted-foreground">{showRejected ? "▲ Hide" : "▼ Show"}</span>
                  </CardTitle>
                </CardHeader>
                {showRejected && (
                  <CardContent>
                    <div className="space-y-2">
                      {rejectedUsers.map(user => (
                        <div key={user.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-red-50 rounded border border-red-200" data-testid={`rejected-user-${user.user_id}`}>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{user.name || "Unnamed"}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="default"
                              disabled={!!actionLoading}
                              onClick={() => handleApprove(user.user_id)}
                              data-testid={`reapprove-btn-${user.user_id}`}
                            >
                              <Check className="h-3 w-3 mr-1" /> Re-approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={actionLoading === `del-${user.user_id}`}
                              onClick={() => handleDeleteUser(user.user_id)}
                              data-testid={`delete-btn-${user.user_id}`}
                            >
                              {actionLoading === `del-${user.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div className="pt-2 border-t">
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={actionLoading === "del-all"}
                          onClick={handleDeleteAllRejected}
                          data-testid="delete-all-rejected-btn"
                          className="w-full"
                        >
                          {actionLoading === "del-all" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <X className="h-3 w-3 mr-1" />}
                          Delete All Rejected Users
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            )}
          </>
        )}
      </div>

      {/* Set Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-600" />
              Set Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 chars)"
                data-testid="set-password-input"
              />
              <p className="text-xs text-muted-foreground">
                User will be logged out and must login with this new password.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleSetPassword}
              disabled={!newPassword || newPassword.length < 6 || actionLoading?.startsWith('pwd-')}
              data-testid="confirm-set-password-btn"
            >
              {actionLoading?.startsWith('pwd-') ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Key className="h-4 w-4 mr-2" />
              )}
              Set Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Add User
            </DialogTitle>
            <DialogDescription>
              Create a new user account. They can log in immediately with the credentials you set here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="add-user-email">Email</Label>
              <Input
                id="add-user-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                data-testid="add-user-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-user-name">Full Name</Label>
              <Input
                id="add-user-name"
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Jane Doe"
                data-testid="add-user-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-user-password">Password</Label>
              <Input
                id="add-user-password"
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="min 6 characters"
                data-testid="add-user-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-user-role">Role</Label>
              <select
                id="add-user-role"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                data-testid="add-user-role"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="add-user-approved"
                type="checkbox"
                checked={newUser.approved}
                onChange={(e) => setNewUser({ ...newUser, approved: e.target.checked })}
                data-testid="add-user-approved"
              />
              <Label htmlFor="add-user-approved" className="text-sm cursor-pointer">
                Approve immediately (user can log in right away)
              </Label>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleCreateUser}
              disabled={actionLoading === "create-user"}
              data-testid="confirm-add-user-btn"
            >
              {actionLoading === "create-user" ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
